import React from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Typography, IconButton, Divider } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import PeopleIcon from '@mui/icons-material/People';
import StoreIcon from '@mui/icons-material/Store';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import WebIcon from '@mui/icons-material/Web';
import EmailIcon from '@mui/icons-material/Email';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import HandshakeIcon from '@mui/icons-material/Handshake';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CategoryIcon from '@mui/icons-material/Category';
import { adminLogout } from '../../lib/adminAuth';

const DRAWER_WIDTH = 240;

// Grouped so it's clear which surface each screen affects:
//  - Landing Page: the public marketing site
//  - Web Portal: the vendor/business dashboard
//  - Mobile App: the customer-facing app
//  - Platform: cross-cutting settings that affect multiple surfaces
const NAV_GROUPS = [
  {
    heading: null,
    items: [
      { label: 'Dashboard', path: '/admin', icon: <DashboardIcon />, exact: true },
    ],
  },
  {
    heading: 'Landing Page',
    items: [
      { label: 'Content', path: '/admin/landing-page', icon: <WebIcon /> },
      { label: 'Business Categories', path: '/admin/business-categories', icon: <CategoryIcon /> },
      { label: 'Blog Posts', path: '/admin/blogs', icon: <EditNoteIcon /> },
    ],
  },
  {
    heading: 'Web Portal',
    items: [
      { label: 'Vendors', path: '/admin/vendors', icon: <StoreIcon /> },
      { label: 'Collaborations', path: '/admin/collaborations', icon: <HandshakeIcon /> },
      { label: 'Credits & Pricing', path: '/admin/credits', icon: <MonetizationOnIcon /> },
      { label: 'Help & FAQ', path: '/admin/help', icon: <HelpOutlineIcon /> },
    ],
  },
  {
    heading: 'Mobile App',
    items: [
      { label: 'Customers', path: '/admin/customers', icon: <PeopleIcon /> },
      { label: 'Resources', path: '/admin/resources', icon: <ArticleIcon /> },
    ],
  },
  {
    heading: 'Platform',
    items: [
      { label: 'Site Settings', path: '/admin/settings', icon: <SettingsIcon /> },
      { label: 'Email Templates', path: '/admin/emails', icon: <EmailIcon /> },
    ],
  },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login', { replace: true });
  };

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#fafafa' }}>
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            bgcolor: '#fff',
            borderRight: '1px solid #f0f0f0',
          },
        }}
      >
        <Box sx={{ p: 3, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: 18 }}>Rewarding</Typography>
          <Typography variant="caption" sx={{ color: '#999' }}>Admin Portal</Typography>
        </Box>
        <Divider sx={{ mx: 2, mb: 1 }} />
        <List sx={{ px: 1.5 }}>
          {NAV_GROUPS.map((group, gi) => (
            <React.Fragment key={group.heading || `group-${gi}`}>
              {group.heading && (
                <Typography
                  variant="overline"
                  sx={{ display: 'block', px: 2, mt: gi === 0 ? 0 : 1.5, mb: 0.5, color: '#bbb', fontSize: 10, fontWeight: 700, letterSpacing: 0.8 }}
                >
                  {group.heading}
                </Typography>
              )}
              {group.items.map((item) => (
                <ListItemButton
                  key={item.path}
                  component={Link}
                  to={item.path}
                  selected={isActive(item)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    '&.Mui-selected': { bgcolor: '#f5f5f7', color: '#1a1a2e' },
                    '&.Mui-selected .MuiListItemIcon-root': { color: '#1a1a2e' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: '#999' }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }} />
                </ListItemButton>
              ))}
            </React.Fragment>
          ))}
        </List>

        <Box sx={{ mt: 'auto', p: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <ListItemButton onClick={handleLogout} sx={{ borderRadius: 2 }}>
            <ListItemIcon sx={{ minWidth: 36, color: '#999' }}><LogoutIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Log Out" primaryTypographyProps={{ fontSize: 13, color: '#999' }} />
          </ListItemButton>
        </Box>
      </Drawer>

      {/* Main content */}
      <Box component="main" sx={{ flexGrow: 1, p: 4, maxWidth: 1200 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
